import { Splide, SplideSlide } from '@splidejs/react-splide';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import Icon from 'dpl/common/components/Icon';

export const SPLIDE_OPTIONS = {
  arrows: false,
  dragMinThreshold: 20,
  flickMaxPages: 1,
  flickPower: 300,
  gap: 16,
  keyboard: 'focused',
  pagination: false,
  perPage: 1,
  snap: true
};

function Carousel({ puppies }) {
  const splideRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const puppyCount = puppies.length;
  const isNextButtonDisabled = activeIndex >= puppyCount - 1;
  const isPrevButtonDisabled = activeIndex <= 0;

  function handleActiveSlide(slide) {
    setActiveIndex(slide.index);
  }

  return (
    <div className="CoverageExamplesSlider__wrapper">
      <Splide
        className="relative"
        options={{
          ...SPLIDE_OPTIONS,
          drag: puppyCount > 0,
          start: activeIndex
        }}
        ref={splideRef}
        onActive={handleActiveSlide}
      >
        {puppies.map(puppy => (
          <SplideSlide key={puppy.name} className="puppy-info">
            <div className="row slim-gutters">
              <div className="col-7">
                <div
                  className="puppy-info__photo card cover bg-center relative h-100"
                  style={{
                    backgroundImage: `url(${puppy.profile_photo_url})`
                  }}
                >
                  <span className="puppy-info__badge">{puppy.name}</span>
                </div>
              </div>
              <div className="col-5">
                <div
                  className={classnames(
                    'puppy-info__details row slim-gutters',
                    puppy.gallery_photo_url &&
                      'puppy-info__details--gallery-photo'
                  )}
                >
                  <div className="col-12">
                    <div
                      className="puppy-info__card card w-100 cover bg-center relative h-100"
                      style={{
                        backgroundImage: puppy.gallery_photo_url
                          ? `url(${puppy.gallery_photo_url})`
                          : null
                      }}
                    >
                      {puppy.condition && (
                        <p className="puppy-info__description font-serif">
                          {puppy.condition}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="puppy-info__card card w-100">
                      {puppy.charges.map((charge, chargeIndex) => (
                        <div key={chargeIndex} className="puppy-info__charge">
                          {charge.amount && (
                            <p className="puppy-info__charge__amount font-serif">
                              {charge.amount}
                            </p>
                          )}
                          <p
                            className={classnames(
                              'puppy-info__charge__description',
                              !charge.amount && 'no-amount'
                            )}
                          >
                            {charge.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </SplideSlide>
        ))}
      </Splide>
      {puppies.length > 1 && (
        <div className="HorizontalSlider__arrows mt3 mt7-md">
          <button
            type="button"
            className={classnames('HorizontalSliderArrow left-0', {
              'o-60': isPrevButtonDisabled
            })}
            disabled={isPrevButtonDisabled}
            onClick={() => splideRef.current.go('<')}
          >
            <div className="HorizontalSliderArrow__left flex items-center justify-center ph3-lg">
              <Icon name="fetch-arrow-left" />
            </div>
          </button>
          <button
            type="button"
            className={classnames('HorizontalSliderArrow right-0', {
              'o-60': isNextButtonDisabled
            })}
            disabled={isNextButtonDisabled}
            onClick={() => splideRef.current.go('>')}
          >
            <div className="HorizontalSliderArrow__right flex items-center justify-center ph3-lg">
              <Icon name="fetch-arrow-right" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

export default function CoverageExamples() {
  const coverageExamples = useSelector(
    ({ server }) => server.coverage_examples
  );

  return (
    <div className="CoverageExamples">
      {coverageExamples.map(({ heading, description, puppies }) => (
        <div key={heading} className="section-coverage-day-one__row row">
          <div className="col-xl-6">
            <div className="section-coverage-day-one__info">
              <h3 className="heading-3">{heading}</h3>
              <p className="secondary lh-copy mt3 mt6-lg">{description}</p>
            </div>
          </div>
          <div className="col-xl-6 mt10">
            <Carousel puppies={puppies} />
          </div>
        </div>
      ))}
    </div>
  );
}

Carousel.propTypes = {
  puppies: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      profile_photo_url: PropTypes.string.isRequired,
      gallery_photo_url: PropTypes.string,
      condition: PropTypes.string,
      charges: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.string,
          description: PropTypes.string.isRequired
        })
      ).isRequired
    })
  ).isRequired
};
